if (!customElements.get('product-sticky-purchase-bar')) {
  customElements.define('product-sticky-purchase-bar', class ProductStickyPurchaseBar extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.unsubscribe = null;
      this.observer = null;
      this.syncFrame = null;
      this.heightFrame = null;
      this.visibilityFrame = null;
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      document.removeEventListener('change', this.handleDocumentChange);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll);

      if (this.syncFrame !== null) {
        cancelAnimationFrame(this.syncFrame);
        this.syncFrame = null;
      }

      if (this.heightFrame !== null) {
        cancelAnimationFrame(this.heightFrame);
        this.heightFrame = null;
      }

      if (this.visibilityFrame !== null) {
        cancelAnimationFrame(this.visibilityFrame);
        this.visibilityFrame = null;
      }

      this.clearOwnedHeight();

      this.initialized = false;
    }

    initialize() {
      if (this.initialized) return;

      this.variantDataNode = this.querySelector('.js-sticky-variant-data');
      this.variantInput = this.querySelector('.js-sticky-variant-id');
      this.priceNode = this.querySelector('.js-sticky-price');
      this.comparePriceNode = this.querySelector('.js-sticky-compare-price');
      this.submitButton = this.querySelector('.js-sticky-submit');

      try {
        this.variants = JSON.parse(this.variantDataNode?.textContent || '');
      } catch (error) {
        this.disableBar();
        this.initialized = true;
        return;
      }

      if (!Array.isArray(this.variants) || !this.variantInput || !this.priceNode || !this.submitButton) {
        this.disableBar();
        this.initialized = true;
        return;
      }

      this.mainProductInfo = document.querySelector('product-info[id^="MainProduct-"]')
        || Array.from(document.querySelectorAll('product-info')).find((element) => !this.contains(element));
      this.originalPurchaseArea = this.mainProductInfo?.querySelector('.product-form');

      if (!this.mainProductInfo || !this.originalPurchaseArea) {
        this.disableBar();
        this.initialized = true;
        return;
      }

      this.buttonLabelNode = Array.from(this.submitButton.children).find((child) => child.matches('span'));
      const initialVariant = this.variants.find((item) => String(item.id) === String(this.variantInput.value));
      this.initialButtonLabel = initialVariant?.available ? this.buttonLabelNode?.textContent.trim() : '';

      if (typeof subscribe === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
          const variant = payload?.data?.variant;
          if (variant) this.syncVariant(variant.id);
        });
      }

      document.addEventListener('change', this.handleDocumentChange);
      this.observer = new IntersectionObserver(() => this.updateVisibility());
      this.observer.observe(this.originalPurchaseArea);
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('scroll', this.handleScroll, { passive: true });

      this.initialized = true;
      this.syncFromMainForm();
      this.updateVisibility();
    }

    disableBar() {
      this.setVisible(false);
      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
      }
    }

    handleDocumentChange(event) {
      if (!this.mainProductInfo?.contains(event.target) || this.contains(event.target)) return;

      if (this.syncFrame !== null) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.syncFromMainForm();
      });
    }

    handleResize() {
      this.updateVisibility();
    }

    handleScroll() {
      if (this.visibilityFrame !== null) return;

      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    }

    syncFromMainForm() {
      const variantInput = this.originalPurchaseArea?.querySelector('input[name="id"]');
      if (variantInput) this.syncVariant(variantInput.value);
    }

    syncVariant(variantId) {
      const variant = this.variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return;

      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.priceNode.textContent = variant.price;

      if (this.comparePriceNode) {
        this.comparePriceNode.textContent = variant.compareAt || '';
        this.comparePriceNode.hidden = !variant.compareAt;
      }

      const unavailable = !variant.available;
      this.submitButton.disabled = unavailable;
      this.submitButton.setAttribute('aria-disabled', String(unavailable));

      if (unavailable) {
        const mainSubmit = this.mainProductInfo.querySelector('.product-form__submit');
        const mainLabel = Array.from(mainSubmit?.children || []).find((child) => child.matches('span'))?.textContent.trim();
        if (mainLabel && this.buttonLabelNode) this.buttonLabelNode.textContent = mainLabel;
      } else if (this.initialButtonLabel && this.buttonLabelNode) {
        this.buttonLabelNode.textContent = this.initialButtonLabel;
      }
    }

    updateVisibility() {
      if (!this.originalPurchaseArea) {
        this.setVisible(false);
        return;
      }

      const { boundingClientRect } = { boundingClientRect: this.originalPurchaseArea.getBoundingClientRect() };
      this.setVisible(boundingClientRect.bottom < 0);
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));

      if (!visible) {
        if (this.heightFrame !== null) {
          cancelAnimationFrame(this.heightFrame);
          this.heightFrame = null;
        }
        this.clearOwnedHeight();
        return;
      }

      if (this.heightFrame !== null) cancelAnimationFrame(this.heightFrame);
      this.heightFrame = requestAnimationFrame(() => {
        this.heightFrame = null;
        if (!this.isConnected || !this.classList.contains('is-visible')) return;

        const value = this.offsetHeight + 'px';
        if (this.offsetHeight > 0) {
          document.documentElement.__productStickyPurchaseBarHeightOwner = this;
          document.documentElement.style.setProperty('--product-sticky-bar-height', value);
        } else {
          this.clearOwnedHeight();
        }
      });
    }

    clearOwnedHeight() {
      if (document.documentElement.__productStickyPurchaseBarHeightOwner === this) {
        document.documentElement.style.setProperty('--product-sticky-bar-height', '0px');
        document.documentElement.__productStickyPurchaseBarHeightOwner = null;
      }
    }
  });
}

if (!window.__productStickyPurchaseBarSectionLoadBound) {
  window.__productStickyPurchaseBarSectionLoadBound = true;
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    const bars = [
      ...(section.matches?.('product-sticky-purchase-bar') ? [section] : []),
      ...section.querySelectorAll?.('product-sticky-purchase-bar') || [],
    ];

    bars.forEach((bar) => {
      if (!bar.initialized) bar.initialize();
    });
  });
}
